import React, { useState, useEffect } from 'react';
import { FaTrash, FaEdit, FaPlus, FaTimes, FaHome, FaDownload } from 'react-icons/fa';
import { HomeURL } from '../services/Webservice';

function Banners() {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [formData, setFormData] = useState({ Status: true, ImageFile: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => { fetchBanners(); }, []);

    const fetchBanners = async () => {
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append('Param', '[]');
            fd.append('Flag', '4'); // Flag 4 is Select All in this project

            const response = await fetch(`${HomeURL}api/Values/Banners_CRUD`, {
                method: 'POST',
                body: fd
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

            const text = await response.text();
            try {
                const data = JSON.parse(text);
                if (Array.isArray(data)) {
                    const formattedBanners = data.map(b => ({
                        bannerid: b.Bannerid || 0,
                        imageUrl: b.Bannerimage || '',
                        status: b.Status,
                        createdDate: b.CreatedDate
                    }));
                    setBanners(formattedBanners);
                } else {
                    console.warn('API did not return an array:', data);
                }
            } catch (e) {
                console.error('JSON Parse Error:', text);
                throw new Error('API returned invalid data format');
            }
        } catch (error) {
            console.error('Banners Fetch Error:', error);
            alert(`Unable to load banners: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const formatDateBox = (dateString) => {
        if (!dateString) return 'N/A';
        const dateObj = new Date(dateString);
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = dateObj.toLocaleString('en-US', { month: 'short' });
        const year = dateObj.getFullYear();
        let hours = dateObj.getHours();
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? String(hours).padStart(2, '0') : '12'; // the hour '0' should be '12'

        return `${day}-${month}-${year},${hours}:${minutes}:${ampm}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            let bannerImagePath = editingBanner ? editingBanner.imageUrl : '';

            // 1. Upload image if a new one is selected
            if (formData.ImageFile) {
                const uploadFd = new FormData();
                uploadFd.append('PostedFile', formData.ImageFile);

                const uploadRes = await fetch(`${HomeURL}api/Values/UploadImage`, {
                    method: 'POST',
                    body: uploadFd
                });

                if (uploadRes.ok) {
                    bannerImagePath = await uploadRes.json();
                    if (!bannerImagePath) throw new Error('Image upload returned no path');
                } else {
                    const errorText = await uploadRes.text();
                    throw new Error(`Upload failed (${uploadRes.status}): ${errorText || uploadRes.statusText}`);
                }
            }

            // 2. Perform CRUD (Insert or Update)
            const bannerData = [{
                Bannerid: editingBanner ? editingBanner.bannerid : 0,
                Bannerimage: bannerImagePath,
                Status: formData.Status,
                CreatedBy: parseInt(localStorage.getItem('userId') || '0')
            }];

            const fd = new FormData();
            fd.append('Param', JSON.stringify(bannerData));
            fd.append('Flag', editingBanner ? '2' : '1'); // 1 = Insert, 2 = Update

            const response = await fetch(`${HomeURL}api/Values/Banners_CRUD`, {
                method: 'POST',
                body: fd
            });

            if (response.ok) {
                const result = await response.text();
                setShowModal(false);
                fetchBanners();
                alert(editingBanner ? 'Banner updated successfully' : 'Banner created successfully');
                setEditingBanner(null);
                setFormData({ Status: true, ImageFile: null });
            } else {
                alert("Error saving banner");
            }
        } catch (error) {
            console.error(error);
            alert('Failed to save: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditClick = (banner) => {
        setEditingBanner(banner);
        setFormData({ Status: banner.status, ImageFile: null });
        setShowModal(true);
    };

    const handleDelete = async (banner) => {
        if (!window.confirm(`Delete this Banner?`)) return;

        try {
            const bannerData = [{
                Bannerid: banner.bannerid
            }];

            const fd = new FormData();
            fd.append('Param', JSON.stringify(bannerData));
            fd.append('Flag', '3'); // 3 = Delete

            const response = await fetch(`${HomeURL}api/Values/Banners_CRUD`, {
                method: 'POST',
                body: fd
            });

            if (response.ok) {
                fetchBanners();
            } else {
                alert('Failed to delete');
            }
        } catch (error) {
            console.error('Delete error', error);
        }
    };

    const handleStatusToggle = async (banner) => {
        try {
            const bannerData = [{
                Bannerid: banner.bannerid,
                Bannerimage: banner.imageUrl,
                Status: !banner.status
            }];

            const fd = new FormData();
            fd.append('Param', JSON.stringify(bannerData));
            fd.append('Flag', '2'); // Update

            const response = await fetch(`${HomeURL}api/Values/Banners_CRUD`, {
                method: 'POST',
                body: fd
            });

            if (response.ok) {
                fetchBanners();
            }
        } catch (error) {
            console.error('Toggle error', error);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, ImageFile: file });
        }
    };

    const getImageUrl = (url) => {
        if (!url) return '';
        if (url.startsWith('http')) return url;
        // Strip any leading slashes to avoid double slashes with HomeURL
        const cleanPath = url.replace(/^\//, '');
        return `${HomeURL}${cleanPath}`;
    };

    // Very simple filter logic
    const filteredBanners = banners.filter(banner => {
        if (!searchTerm) return true;
        const dateStr = formatDateBox(banner.createdDate).toLowerCase();
        return dateStr.includes(searchTerm.toLowerCase());
    });

    return (
        <div style={{ padding: '0px 20px', fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif', background: '#f4f6f9', minHeight: '100vh' }}>

            {/* Breadcrumb */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '15px 0', borderBottom: '1px solid #ddd', color: '#111827', fontWeight: '500' }}>
                <FaHome style={{ color: '#0d6efd', marginRight: '8px', fontSize: '18px' }} />
                <span style={{ color: '#0d6efd', fontWeight: '600' }}>Home</span>
                <span style={{ margin: '0 8px', color: '#9ca3af' }}>/</span>
                <span>Banners</span>
            </div>

            <div style={{ background: '#fff', marginTop: '20px', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb' }}>

                {/* Toolbar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', backgroundColor: '#f0f5fa', borderTopLeftRadius: '4px', borderTopRightRadius: '4px', borderBottom: '1px solid #d1d5db' }}>
                    <div style={{ display: 'flex', gap: '15px' }}>
                        <FaPlus
                            onClick={() => { setEditingBanner(null); setFormData({ Status: true, ImageFile: null }); setShowModal(true); }}
                            style={{ fontSize: '22px', color: '#4b5563', cursor: 'pointer' }}
                        />
                        <FaDownload style={{ fontSize: '22px', color: '#4c51bf', cursor: 'pointer' }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '14px', color: '#374151' }}>{filteredBanners.length} of {banners.length}</span>
                        <div style={{ position: 'relative' }}>
                            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>🔍</span>
                            <input
                                type="text"
                                placeholder="Global Filter"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ padding: '6px 10px 6px 30px', border: '1px solid #d1d5db', borderRadius: '4px', width: '200px', outline: 'none' }}
                            />
                        </div>
                    </div>
                </div>

                {/* Table */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr style={{ background: '#d4e6f1', textAlign: 'left', color: '#1e40af' }}>
                            <th style={{ padding: '12px 20px', fontWeight: '600', borderBottom: '1px solid #bfdbfe' }}>Action</th>
                            <th style={{ padding: '12px 20px', fontWeight: '600', borderBottom: '1px solid #bfdbfe' }}>Banner Image ↑↓</th>
                            <th style={{ padding: '12px 20px', fontWeight: '600', borderBottom: '1px solid #bfdbfe' }}>Added on ↑↓</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="3" style={{ padding: '20px', textAlign: 'center' }}>Loading...</td></tr>
                        ) : filteredBanners.length === 0 ? (
                            <tr><td colSpan="3" style={{ padding: '20px', textAlign: 'center' }}>No banners found.</td></tr>
                        ) : (
                            filteredBanners.map((banner, index) => (
                                <tr key={banner.bannerid} style={{ backgroundColor: index % 2 !== 0 ? '#f0f4f8' : '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '12px 20px', width: '200px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <button
                                                onClick={() => handleEditClick(banner)}
                                                style={{ border: '1px solid #6b7280', background: 'transparent', borderRadius: '3px', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >
                                                <FaEdit style={{ color: '#4b5563', fontSize: '14px' }} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(banner)}
                                                style={{ border: '1px solid #dc2626', background: 'transparent', borderRadius: '3px', padding: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >
                                                <FaTrash style={{ color: '#dc2626', fontSize: '14px' }} />
                                            </button>

                                            {/* Toggle Switch */}
                                            <div
                                                onClick={() => handleStatusToggle(banner)}
                                                style={{
                                                    width: '40px',
                                                    height: '20px',
                                                    backgroundColor: banner.status ? '#3b82f6' : '#d1d5db',
                                                    borderRadius: '20px',
                                                    position: 'relative',
                                                    cursor: 'pointer',
                                                    transition: 'background-color 0.3s'
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        backgroundColor: '#fff',
                                                        borderRadius: '50%',
                                                        position: 'absolute',
                                                        top: '2px',
                                                        left: banner.status ? '22px' : '2px',
                                                        transition: 'left 0.3s'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px 20px' }}>
                                        {banner.imageUrl ? (
                                            <img src={getImageUrl(banner.imageUrl)} alt="banner" style={{ height: '35px', objectFit: 'contain', border: '1px solid #ddd', padding: '2px', background: '#fff' }} />
                                        ) : (
                                            "No Image"
                                        )}
                                    </td>
                                    <td style={{ padding: '12px 20px', color: '#4b5563' }}>
                                        {formatDateBox(banner.createdDate)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Pagination (Visual only for now matching screenshot) */}
                <div style={{ display: 'flex', justifyContent: 'center', padding: '15px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'flex', alignItems: 'center', color: '#9ca3af', gap: '15px', fontSize: '14px' }}>
                        <span style={{ cursor: 'not-allowed' }}>«</span>
                        <span style={{ cursor: 'not-allowed' }}>‹</span>
                        <span style={{ background: '#eff6ff', color: '#3b82f6', padding: '4px 12px', borderRadius: '4px', fontWeight: 'bold' }}>1</span>
                        <span style={{ cursor: 'not-allowed' }}>›</span>
                        <span style={{ cursor: 'not-allowed' }}>»</span>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', padding: '30px', borderRadius: '8px', width: '400px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '15px', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, fontSize: '20px' }}>{editingBanner ? 'Update' : 'Add'} Banner</h2>
                            <FaTimes onClick={() => setShowModal(false)} style={{ cursor: 'pointer', fontSize: '20px', color: '#666' }} />
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Banner Image {editingBanner && '(Leave blank to keep current)'}</label>
                                <input
                                    type="file"
                                    accept="image/*,.webp"
                                    onChange={handleFileChange}
                                    required={!editingBanner}
                                    style={{ display: 'block', width: '100%', marginBottom: '10px' }}
                                />
                                {editingBanner && editingBanner.imageUrl && (
                                    <div style={{ marginTop: '10px' }}>
                                        <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Current Image:</label>
                                        <img src={getImageUrl(editingBanner.imageUrl)} alt="Current Banner" style={{ height: '60px', borderRadius: '4px', border: '1px solid #ddd' }} />
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                                <label style={{ margin: 0, fontWeight: 'bold' }}>Active Status:</label>
                                <input
                                    type="checkbox"
                                    checked={formData.Status}
                                    onChange={(e) => setFormData({ ...formData, Status: e.target.checked })}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                style={{ width: '100%', padding: '12px', background: isSubmitting ? '#93c5fd' : '#3b82f6', color: '#fff', border: 'none', borderRadius: '4px', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: 'bold' }}>
                                {isSubmitting ? (editingBanner ? 'Updating...' : 'Saving...') : (editingBanner ? 'Update Banner' : 'Save Banner')}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Banners;
